/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2020 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import { createSelector } from 'reselect';
import { selectGroupLevelSetting } from '@nebula/airglow-plugin-unified-shell';
import { DEFAULT_VIEW_PLUGINS } from '../../casing/data/plugins';
import { selectSessionRouteParam } from './session.selectors';

const selectOrgSetting = setting => selectGroupLevelSetting('griffon', 'org', setting);
const selectUserOrgSetting = setting => selectGroupLevelSetting('griffon', 'userorg', setting);
const selectUserSetting = setting => selectGroupLevelSetting('griffon', 'user', setting);

// explicitly returning a function so selectGroupLevelSetting can be mocked
export const selectRecentSessions = state => selectUserSetting('recentSessions')(state);
export const selectSessionSettings = state => selectOrgSetting('sessionSettings')(state);
export const selectUserValidationPluginSettings = state => selectUserOrgSetting('validationPlugins')(state);
export const selectUserViewPlugins = state => selectUserOrgSetting('enabledViewPlugins')(state);

export const selectOrgValidationPluginSettings = (state) => {
  const settings = selectOrgSetting('validationPlugins')(state);
  return settings;
};

export const selectOrgViewPlugins = (state) => {
  const settings = selectOrgSetting('enabledViewPlugins')(state);
  return settings && !R.isEmpty(settings) ? settings : DEFAULT_VIEW_PLUGINS;
};

export const selectSessionIdSettings = createSelector(
  selectSessionRouteParam,
  selectSessionSettings,
  R.propOr({})
);

export const selectSessionValidationPluginSettings = createSelector(
  selectSessionIdSettings,
  R.prop('validationPlugins')
);

export const selectSessionViewPlugins = createSelector(
  selectSessionIdSettings,
  R.prop('enabledViewPlugins')
);

const userSettingsFallback = (sessionSetting, userSetting, defaults) => (!R.isEmpty(sessionSetting)
  ? sessionSetting : !R.isEmpty(userSetting)
    ? userSetting : defaults);

export const selectUserWorkspaceSettings = createSelector(
  selectUserOrgSetting('workspaces'),
  selectSessionViewPlugins,
  selectUserViewPlugins,
  selectSessionValidationPluginSettings,
  selectUserValidationPluginSettings,
  (userWorkspaces, sessionViewPlugins, userViewPlugins, sessionValidations, userValidations) => {
    let workspaces = userWorkspaces || [];

    if (R.isEmpty(workspaces) && !R.isEmpty(userViewPlugins)) {
      workspaces = [{
        id: 'my-workspace',
        name: 'My Workspace',
        type: 'user',
        enabledViewPlugins: userSettingsFallback(sessionViewPlugins, userViewPlugins, []),
        validationPlugins: userSettingsFallback(sessionValidations, userValidations, {})
      }];
    }

    return workspaces;
  }
);

export const selectOrgWorkspaceSettings = createSelector(
  selectOrgSetting('workspaces'),
  selectOrgViewPlugins,
  selectOrgValidationPluginSettings,
  (orgWorkspaces, orgViewPlugins, orgValidations) => {
    let workspaces = orgWorkspaces || [];

    if (R.isEmpty(workspaces) && !R.isEmpty(orgViewPlugins)) {
      workspaces = [{
        id: 'my-org-workspace',
        name: 'My Org Workspace',
        type: 'org',
        enabledViewPlugins: orgViewPlugins || [],
        validationPlugins: orgValidations || {}
      }];
    }

    return workspaces;
  }
);
